/**
 * useHouseAllocation hook
 * Fetches kanban board data for one course and provides move mutations.
 * Supports both students and cúntóirí.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export function useHouseAllocation(courseUuid) {
  const queryClient = useQueryClient();
  const queryKey = ['allocation', courseUuid];

  const query = useQuery({
    queryKey,
    queryFn: () => api.allocation.getData(courseUuid),
    enabled: !!courseUuid,
    staleTime: 30 * 1000,
  });

  const moveMutation = useMutation({
    mutationFn: ({ studentUuids, houseUuid, cuntoirUuids }) =>
      api.allocation.move(studentUuids, houseUuid, cuntoirUuids),
    onMutate: async ({ studentUuids, houseUuid, cuntoirUuids = [] }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;

        // Update students' house_uuid
        const updatedStudents = old.students.map((s) =>
          studentUuids.includes(s.uuid) ? { ...s, house_uuid: houseUuid } : s
        );

        // Update cúntóirí house_uuid
        const updatedCuntoirs = (old.cuntoirs ?? []).map((c) =>
          cuntoirUuids.includes(c.uuid) ? { ...c, house_uuid: houseUuid } : c
        );

        // Update house student lists
        const allMovingUuids = [...studentUuids, ...cuntoirUuids];
        const updatedHouses = old.houses.map((h) => {
          let students = h.students.filter((u) => !allMovingUuids.includes(u));
          if (houseUuid && h.uuid === houseUuid) {
            students = [...students, ...allMovingUuids];
          }
          return {
            ...h,
            students,
            current_occupancy: students.length,
            available_spots: Math.max(0, h.course_capacity - students.length),
          };
        });

        return { ...old, students: updatedStudents, cuntoirs: updatedCuntoirs, houses: updatedHouses };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['allocation', 'summary'] });
    },
  });

  return {
    // Data
    course: query.data?.course ?? null,
    houses: query.data?.houses ?? [],
    students: query.data?.students ?? [],
    cuntoirs: query.data?.cuntoirs ?? [],

    // State
    isLoading: query.isLoading,
    error: query.error,

    // Actions
    moveStudents: (studentUuids, houseUuid, cuntoirUuids = []) =>
      moveMutation.mutateAsync({ studentUuids, houseUuid, cuntoirUuids }),
    isMoving: moveMutation.isPending,
    moveError: moveMutation.error,
  };
}

export default useHouseAllocation;
